export const rfiArticles = [
    {
        id: 'rfi.overview',
        title: 'RFI Overview',
        category: 'RFIs',
        order: 1,
        summary: 'Request for Information - track and manage project queries',
        content: `
The RFI (Request for Information) page lets you create, track, and resolve queries across your projects. RFIs are used to formally request clarification on drawings, specifications, or any other project matter from consultants, subcontractors, or internal team members.

**Key Features:**

- Create RFIs linked to a specific job
- Assign priority, type, and project stage
- Send RFIs via email to recipients (no login required for responders)
- Track status through a customisable workflow (Draft, Open, In Progress, Resolved, Closed)
- Add internal notes and discussion threads
- Filter and search by status, type, stage, or keyword
    `,
        expandableSections: [
            {
                title: 'Creating an RFI',
                content: `
1. Go to the **RFIs** page
2. Click **New RFI**
3. Fill in the form:
   - **Subject** — A clear, concise title for the query
   - **Description** — Full detail of what information is needed
   - **Job** — Link to the relevant job
   - **Type** — Error, Warning, Info, Issue, Request, Solution, or Remark
   - **Priority** — Very Low through to Critical/Urgent
   - **Stage** — The project stage (e.g. Construction Documents, Bidding)
   - **Assigned To** — Person responsible for following up
   - **Due Date** — When a response is needed
   - **Labels** — Optional tags for categorisation
   - **Recipients** — Email addresses of people who should respond
4. Click **Create**

The RFI is assigned an automatic number (e.g. RFI-001) and can be sent to recipients immediately.
        `,
            },
            {
                title: 'Sending to Recipients',
                content: `
RFIs can be emailed to external parties for a response:

1. Open an RFI from the list
2. Go to the **Recipients** tab
3. Add recipients by searching your contacts or entering an email
4. Click **Send** to email the RFI

Each recipient receives:
- The RFI subject, description, and reference number
- A unique, secure link to the response portal
- No login is required — they click the link and respond directly
        `,
            },
            {
                title: 'Status Workflow',
                content: `
RFIs follow a customisable status workflow:

| Status | Meaning |
|--------|---------|
| **Draft** | Not yet issued — still being prepared |
| **Open** | Issued and awaiting response |
| **In Progress** | Being actively worked on |
| **Assigned** | Delegated to a specific person |
| **Resolved** | Answer received and accepted |
| **Approved** | Formally approved/signed off |
| **Rejected** | Response was not acceptable |
| **Closed** | Completed — no further action needed |
| **Deprecated** | No longer relevant |

Click the status badge on an RFI to change its status directly.
        `,
            },
            {
                title: 'Notes & Discussion',
                content: `
Each RFI has a **Notes** tab for internal discussion:

- Add timestamped notes as the RFI progresses
- Notes are visible to all team members with access
- Use notes to record decisions, phone conversations, or follow-up actions
- Notes support plain text content
        `,
            },
            {
                title: 'Types & Priorities',
                content: `
**Types** categorise the nature of the query:
- **Error** — Something is wrong in the documents
- **Warning** — Potential issue that needs verification
- **Info** — General information request
- **Issue** — Known problem requiring resolution
- **Request** — Formal request for information or action
- **Solution** — Proposed solution for review
- **Remark** — General observation or comment

**Priorities** indicate urgency:
- Very Low, Low, Medium, High, Critical, Urgent

Types, statuses, stages, and priorities are customisable via the RFI settings.
        `,
            },
        ],
        keywords: ['rfi', 'request for information', 'query', 'clarification', 'issue', 'project query'],
        route: '/rfis',
        relatedArticles: ['rfi.response-portal', 'rfq.overview', 'jobs.overview'],
        screenshots: [
            {
                src: 'rfi/rfi-create-modal.png',
                alt: 'Create RFI modal',
                caption: 'Creating a new RFI with subject, description, type, priority, and recipients',
            },
        ],
    },
    {
        id: 'rfi.response-portal',
        title: 'RFI Response Portal',
        category: 'RFIs',
        order: 2,
        summary: 'How recipients respond to your RFI requests',
        content: `
When you send an RFI, each recipient receives a unique link to view the query and submit their response. No account or login is required.

**What Recipients See:**

- Your company name and details
- The RFI reference number, subject, and full description
- Priority and due date
- A text area to enter their response
- Option to attach files if needed

**How Recipients Respond:**

1. Click the link in the email
2. Review the RFI details
3. Enter their response
4. Click **Submit Response**

Responses are recorded immediately and visible in your RFI detail view.
    `,
        expandableSections: [
            {
                title: 'Portal Security',
                content: `
Each RFI response link is:

- **Unique** — One link per recipient per RFI
- **Token-secured** — Cannot be guessed or shared
- **Tenant-isolated** — Only shows the specific RFI content
- **No login required** — Recipients don't need a Costed.app account
        `,
            },
        ],
        keywords: ['rfi response', 'recipient', 'portal', 'reply', 'answer'],
        route: '/rfis',
        relatedArticles: ['rfi.overview'],
    },
];
